import { get, post, del } from './api';

// Data models
export interface AIAgent {
  id: string;
  name: string;
  description: string;
  default_role: string;
  icon_url?: string;
  skills: string[];
  active: boolean;
  version: string;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  org_id?: string;
}

export interface AssignedAgent {
  agent_id: string;
  name: string;
  description: string;
  icon_url?: string;
  role: string;
  added_by: string;
  added_at: string;
}

export interface AssignAgentRequest {
  agent_id: string;
  role?: string;
}

// Get all available AI agents with optional filtering
export const getAvailableAgents = async (
  params?: {
    active_only?: boolean;
    org_id?: string;
    status?: 'available';
    exclude_project_id?: string;
  }
): Promise<AIAgent[]> => {
  try {
    // Fix for direct API URL request issue
    // Ensure we're using the correct API path format
    let endpoint = '/api/v1/agents';
    
    // Build query string if params are provided
    if (params) {
      const queryParams = new URLSearchParams();
      
      if (params.active_only !== undefined) {
        queryParams.append('active_only', params.active_only.toString());
      }
      
      if (params.org_id) {
        queryParams.append('org_id', params.org_id);
      }
      
      if (params.status) {
        queryParams.append('status', params.status);
      }
      
      if (params.exclude_project_id) {
        queryParams.append('exclude_project_id', params.exclude_project_id);
      }
      
      // Append query string to endpoint if not empty
      const queryString = queryParams.toString();
      if (queryString) {
        endpoint += `?${queryString}`;
      }
    }
    
    // Ensure we're using the API service correctly
    return await get<AIAgent[]>(endpoint);
  } catch (error) {
    console.error('Failed to fetch available agents:', error);
    throw error;
  }
};

// Get a specific AI agent by ID
export const getAgent = async (agentId: string): Promise<AIAgent> => {
  try {
    return await get<AIAgent>(`/api/v1/agents/${agentId}`);
  } catch (error) {
    console.error(`Failed to fetch agent ${agentId}:`, error);
    throw error;
  }
};

// Get AI agents assigned to a project
export const getProjectAgents = async (projectId: string): Promise<AssignedAgent[]> => {
  try {
    // Fix for direct API URL request issue
    // Ensure we're using the correct API path format for project agents
    const endpoint = `/api/v1/projects/${projectId}/agents`;
    return await get<AssignedAgent[]>(endpoint);
  } catch (error) {
    console.error(`Failed to fetch agents for project ${projectId}:`, error);
    throw error;
  }
};

// Assign an agent to a project
export const assignAgentToProject = async (
  projectId: string, 
  agentId: string, 
  role?: string
): Promise<AssignedAgent> => {
  try {
    return await post<AssignedAgent>(`/api/v1/projects/${projectId}/agents`, {
      agent_id: agentId,
      role
    });
  } catch (error) {
    console.error(`Failed to assign agent ${agentId} to project ${projectId}:`, error);
    throw error;
  }
};

// Remove an agent from a project
export const removeAgentFromProject = async (
  projectId: string, 
  agentId: string
): Promise<void> => {
  try {
    await del<void>(`/api/v1/projects/${projectId}/agents/${agentId}`);
  } catch (error) {
    console.error(`Failed to remove agent ${agentId} from project ${projectId}:`, error);
    throw error;
  }
};
